import { useEffect, useId, useMemo, useRef, useState, type FocusEvent, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { useApolloClient } from "@apollo/client/react";
import { useStore } from "zustand";
import { useMe } from "@/auth/use-me";
import { BackButton } from "@/components/common/BackButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Disclosure } from "@/components/design/Disclosure";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { isUuid, type GameSummary, type GoalScope, type GoalState, type GoalView } from "@/lib/games/roleplay/contract";
import { gameHref } from "@/lib/games/roleplay/entry";
import { createRoleplaySession } from "@/lib/games/roleplay/game-session";
import {
  genesisRoleplayRefusal, lockRoleplaySessionPowers, readRoleplaySession, setRoleplaySessionPowers, type GenesisRoleplaySession,
} from "@/lib/games/roleplay/genesis";
import { usePendingScope } from "@/lib/pending-edits";
import {
  canSend,
  heldItems,
  inputOpen,
  pickMissing,
  type Arrival,
  type Newcomer,
  type Draft,
  type Notice,
  type RoleplayGameState,
} from "@/lib/games/roleplay/game-store";
import { characterName, storyClock, storyItems, type StoryMark } from "@/lib/games/roleplay/story";
import { CastPanel } from "./CastPanel";
import { exampleBars, exampleCast, exampleInventory } from "./guide-examples";
import { GoalList } from "./GoalList";
import { GuideCard } from "./GuideCard";
import { InventoryPanel } from "./InventoryPanel";
import { BUSY_BUTTON, LOCKED_BUTTON } from "./locked-button";
import { conditionLabel, noticeText } from "./labels";
import { ChosenPowers } from "./ChosenPowers";
import { PowerBars } from "./PowerBars";
import { StoryFeed } from "./StoryFeed";
import { TurnInput } from "./TurnInput";
import { GameEndSummary } from "./GameEndSummary";

// The roleplay game's own page (decisions 2, 10): the game on the left, the assistant's drawer beside it. The page
// gives the assistant nothing about the game (O11): it registers no assistant context, and the game id rides in the
// query string, never in the route path the drawer reports.

const BACK_TO_PLAY = "/grow/play";

// The tutorial's guide before its first turn, in the assistant tour's style but without an
// assistant: a welcome, then the situation alone, how a move is made, the scene's goals, how the answers move the
// progress, what the inventory holds, who the characters are, and only then the whole game. Each step adds its
// element to the ones before, and from the second step on its card also leads back one step, as the assistant tour's
// „Zurück" does, taking that element away again.
const GUIDE_STEPS = ["welcome", "situation", "turn", "goals", "progress", "inventory", "cast", "done"] as const;
type GuideStep = (typeof GUIDE_STEPS)[number];

// The tour's tint and blur over the page while the guide runs (index.css); the step's element and its card stay sharp.
const GUIDE_SPOTLIGHT = "grow-guide-spotlight";

// How long a box pulses when what it shows has changed, the goals, the inventory or the characters: the tour's three
// pulses of 1.2 s (index.css).
const PULSE_MS = 3600;

// The router state a restart of the scenario hands to the fresh run's page (spark#1075): the player has seen the tour
// already, so the fresh run begins with the game, not the tour.
const TOUR_SEEN_STATE = { roleplayTourSeen: true } as const;

function tourSeen(state: unknown): boolean {
  return typeof state === "object" && state !== null && (state as { roleplayTourSeen?: unknown }).roleplayTourSeen === true;
}

function noop() {
  /* a move is only sent by its own button */
}

// The guide hands over to the game: "Let's go" moves the focus into the field, and the field's box
// wears the guide's outline once, one pulse of 1.2 s that then fades away within 1 s (index.css, .grow-input-handover);
// the class comes off once both are done. With reduced motion the outline stands still and goes sooner.
const HANDOVER_MS = 2200;
const HANDOVER_STILL_MS = 1200;

function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

// How high the area is that the page scrolls in: AppShell's <main>, or the window where the page stands in none.
function visibleHeight(element: HTMLElement): number {
  return element.closest("main")?.clientHeight || window.innerHeight;
}

// How far the story box's end may stand below the end of that area with the player still at it: what a scroll to it
// leaves over in rounding on a zoomed or dense screen.
const AT_END_PX = 2;

// How far the story box's end stands below the end of that area on screen, measured as visibleHeight measures. At or
// above it, the field pinned to the bottom (22) stands in its own place and the story's newest line right above it.
function belowTheEnd(story: HTMLElement): number {
  const main = story.closest("main");
  const end = main?.clientHeight ? main.getBoundingClientRect().top + main.clientHeight : window.innerHeight;
  return story.getBoundingClientRect().bottom - end;
}

// How far that area is scrolled down: AppShell's <main>, or the window where the page stands in none.
function scrolledDown(element: HTMLElement): number {
  const main = element.closest("main");
  return main ? main.scrollTop : window.scrollY;
}

// What the page knows while the story streams: whether the player follows it (`on`), how far down that area stood
// when the page last looked (`seen`), and while the page's own glide to the story box's end is on its way up, where it
// comes to rest (`glideTo`). That glide goes up where the player stands below the end, as on a narrow screen, where the
// side column stands below the story.
interface Follow {
  on: boolean;
  seen: number;
  glideTo: number | null;
}

// Where the player has scrolled to: at the story box's end they follow, and a scroll up while that end stands below the
// visible area stops it, though not one that goes no further than where the page's own glide up comes to rest. That
// glide is over once it is there, or once the area moves down or on up past it.
function lookAtTheScroll(story: HTMLElement, follow: Follow) {
  const now = scrolledDown(story);
  const glide = follow.glideTo;
  if (belowTheEnd(story) <= AT_END_PX) follow.on = true;
  else if (now < follow.seen && (glide === null || now < glide - AT_END_PX)) follow.on = false;
  if (glide !== null && (now > follow.seen || now <= glide + AT_END_PX)) follow.glideTo = null;
  follow.seen = now;
}

const NO_ARRIVALS: readonly Arrival[] = [];
const NO_NEWCOMERS: readonly Newcomer[] = [];

// The goals as the page shows them (play contract §3.5, view 3): the tree flattened into rows of id, both texts, scope
// and state. Scene goal ids are the scene's outcome ids, numbered from o1 in every scene (tutorial build spec), so a new
// scene can repeat them: the texts tell it apart. The story's goals (scope `story`) stand in every scene.
type GoalRow = [id: string, de: string, en: string, scope: GoalScope, state: GoalState];

function goalRows(goals: readonly GoalView[]): GoalRow[] {
  return goals.flatMap((goal): GoalRow[] => [
    [goal.id, goal.text.de, goal.text.en, goal.scope, goal.state],
    ...goalRows(goal.subgoals),
  ]);
}

function goalsShown(goals: readonly GoalView[]): string {
  return JSON.stringify(goalRows(goals));
}

// What a change of the shown goals (both as goalsShown writes them) tells the player (spark#1075, agent-service#129):
// - `scene`: none of the scene goals before is still listed, so a new scene brought them; the goals box says "new"
//   as a whole and the line by the input says the goals have changed. The story's goals never count here, since they
//   stay in every scene;
// - `added`: goals appeared within the scene once their conditions held, such as s2's „Du bittest die
//   Flugbegleiterin, die Lage zu beruhigen“ once the player has spotted her, or the goals a reached door opens (the
//   door itself leaves the list). They are named in the line by the input and marked "new" one by one. A goal that
//   arrives already crossed out is never announced: it was never open to the player, as s2's o7 in the turn that ends
//   the scene by staying out;
// - `states`: only states changed (a goal reached or crossed out) or a goal left the list: the box pulses, no line.
type GoalsChange = { kind: "scene" } | { kind: "added"; ids: string[] } | { kind: "states" };

function goalsChange(before: string, shown: string): GoalsChange {
  const key = ([id, de, en]: GoalRow) => JSON.stringify([id, de, en]);
  const prior = JSON.parse(before) as GoalRow[];
  const now = JSON.parse(shown) as GoalRow[];
  const listed = new Set(now.map(key));
  const priorScene = prior.filter((row) => row[3] === "scene");
  if (priorScene.length > 0 && !priorScene.some((row) => listed.has(key(row)))) return { kind: "scene" };
  const known = new Set(prior.map(key));
  const added = now.filter((row) => !known.has(key(row)) && row[4] !== "crossedOut").map(([id]) => id);
  return added.length > 0 ? { kind: "added", ids: added } : { kind: "states" };
}

export function RoleplayGamePage() {
  const [params] = useSearchParams();
  const gameId = params.get("game");
  // An id that is not a UUID never reaches a request or a path.
  return isUuid(gameId) ? (
    <RoleplayGameGate key={gameId} gameId={gameId} />
  ) : (
    <Frame title={<FormattedMessage id="grow.roleplay.title" defaultMessage="Roleplay" />}>
      <NoticeBox notice={{ code: "notFound", retry: null }} actions={<BackToPlay />} />
    </Frame>
  );
}

// `cleared`: the player has removed every Power. Genesis takes only one to five, so the empty choice stays on this page
// and genesis keeps the last Power it saved until the player picks again; a reload in between shows that saved choice
// again (spark#1236).
type GateState = { kind: "loading" } | { kind: "choose"; session: GenesisRoleplaySession; cleared?: boolean } |
  { kind: "ready" } | { kind: "failed" } | { kind: "notFound" };

// A session whose Powers are final, or that is no longer running, goes straight to the story. Genesis reads an unknown
// session, or one of someone else's, as none at all (RoleplaySessions.mine), so that says the game does not exist.
function gateOf(session: GenesisRoleplaySession | null): GateState {
  if (!session) return { kind: "notFound" };
  return session.powersLocked || session.session.status !== "IN_PROGRESS" ? { kind: "ready" } : { kind: "choose", session };
}

/** The Power choice precedes the first narration, including after a page reload during setup. */
function RoleplayGameGate({ gameId }: { gameId: string }) {
  const client = useApolloClient();
  const [state, setState] = useState<GateState>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // The state above reaches the button only with the next render; the ref turns away a second click before that.
  const starting = useRef(false);
  const emptyHintId = useId();

  useEffect(() => {
    let active = true;
    readRoleplaySession(client, gameId).then(
      (session) => {
        if (active) setState(gateOf(session));
      },
      () => { if (active) setState({ kind: "failed" }); },
    );
    return () => { active = false; };
  }, [client, gameId, attempt]);

  // The refusals of a change or of the lock that genesis's contract leaves to spark (genesis-error-codes.ts): a session
  // that is none of the player's goes back to Play (GENESIS001343); one no longer open (GENESIS001287) or whose Powers
  // are final (GENESIS001344) is read again, and the story then shows it closed or plays with the Powers it holds.
  // Any other refusal stays with the caller's own message.
  const settleRefusal = async (error: unknown): Promise<boolean> => {
    const refusal = genesisRoleplayRefusal(error);
    if (refusal === "not_found") {
      setState({ kind: "notFound" });
      return true;
    }
    if (refusal !== "not_open" && refusal !== "powers_locked") return false;
    try {
      setState(gateOf(await readRoleplaySession(client, gameId)));
    } catch {
      setState({ kind: "failed" });
    }
    return true;
  };

  if (state.kind === "ready") return <RoleplayGame gameId={gameId} />;
  const chosen = state.kind === "choose" && !state.cleared ? state.session.powers : [];
  // No Power chosen, nothing to begin with: the button refuses as it does while busy, so focus stays on it.
  const empty = chosen.length === 0;
  return <Frame title={<FormattedMessage id="grow.roleplay.title" defaultMessage="Roleplay" />}>
    {state.kind === "loading" ? (
      <p className="mt-8 text-smycles-body text-ink-muted" aria-busy="true">
        <FormattedMessage id="common.loading" defaultMessage="Loading…" />
      </p>
    ) : state.kind === "notFound" ? (
      <NoticeBox notice={{ code: "notFound", retry: null }} actions={<BackToPlay />} />
    ) : state.kind === "failed" ? (
      <div className="mt-8 space-y-3">
        <p role="alert" className="text-smycles-body text-ink">
          <FormattedMessage id="grow.roleplay.powers.loadFailed" defaultMessage="The Power selection could not be loaded." />
        </p>
        <Button onClick={() => { setState({ kind: "loading" }); setAttempt((value) => value + 1); }}>
          <FormattedMessage id="common.tryAgain" defaultMessage="Try again" />
        </Button>
        <BackToPlay />
      </div>
    ) : (
      <div className="mt-8 max-w-3xl space-y-6 bg-surface box p-6">
        <h2 className="text-smycles-title text-ink">
          <FormattedMessage id="grow.roleplay.powers.setupTitle" defaultMessage="Choose your Powers" />
        </h2>
        <ChosenPowers powers={chosen} locked={busy} onChange={async (powers) => {
          if (powers.length === 0) {
            setState({ kind: "choose", session: state.session, cleared: true });
            return;
          }
          setBusy(true);
          try {
            const session = await setRoleplaySessionPowers(client, gameId, powers);
            setState({ kind: "choose", session });
          } catch (error) {
            if (!(await settleRefusal(error))) throw error;
          } finally {
            setBusy(false);
          }
        }} />
        {failed ? <p role="alert" className="text-smycles-body text-ink">
          <FormattedMessage id="grow.roleplay.powers.startFailed" defaultMessage="The story could not be started. Try again." />
        </p> : null}
        <div className="flex flex-wrap gap-3">
          {/* While the start runs the button refuses clicks without leaving the tab order, as the Power tiles do, so
              focus stays on it (spark#1236). With no Power chosen it is locked like "Send", with no hover look, and
              while busy it shows no hover either, only the progress cursor in place of the locked one. */}
          <Button
            aria-disabled={busy || empty || undefined}
            aria-busy={busy}
            aria-describedby={empty ? emptyHintId : undefined}
            className={busy ? BUSY_BUTTON : empty ? LOCKED_BUTTON : undefined}
            onClick={async () => {
              if (busy || empty || starting.current) return;
              starting.current = true;
              setBusy(true);
              setFailed(false);
              try {
                await lockRoleplaySessionPowers(client, gameId);
                setState({ kind: "ready" });
              } catch (error) {
                if (!(await settleRefusal(error))) setFailed(true);
              } finally {
                starting.current = false;
                setBusy(false);
              }
            }}
          >
            <FormattedMessage id="grow.roleplay.powers.beginStory" defaultMessage="Begin story" />
          </Button>
          <BackToPlay />
        </div>
        {empty ? <p id={emptyHintId} className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="grow.roleplay.powers.chooseOne"
            defaultMessage="Choose at least one Power to begin the story."
          />
        </p> : null}
      </div>
    )}
  </Frame>;
}

// The page's head is Save Studio's, so the game and Studio are one UI: the hero title, and below it, in Studio's
// description line, what this run is, on Studio's page width (22). As in Studio the hero title is no `h1` element,
// since an `h1` under <main> wears the page title's look (@adr SPARK-0046-one-type-ladder-no-raw-font-sizes@1), not
// the hero's; it is the page's heading all the same, for a screen reader (22).
function Frame({ subtitle, title, children }: { subtitle?: ReactNode; title: ReactNode; children: ReactNode }) {
  return (
    // `min-w-fit` keeps the page's own padding beside the two columns when they are wider than <main> and <main> scrolls
    // sideways (spark#1075); wherever the columns fit, it changes nothing.
    <div className="mt-8 w-full max-w-[1600px] px-4 pb-12 @sm:px-6 @lg:px-10 lg:min-w-fit">
      <div className="mb-5">
        <BackButton />
      </div>
      <BoxHeader
        title={
          <span role="heading" aria-level={1}>
            {title}
          </span>
        }
        variant="hero"
      />
      {subtitle ? <p className="smycles-field-meta text-ink">{subtitle}</p> : null}
      {children}
    </div>
  );
}

function BackToPlay() {
  return (
    <Link to={BACK_TO_PLAY} className={buttonClasses({ variant: "secondary", size: "compact" })}>
      <FormattedMessage id="grow.roleplay.backToPlay" defaultMessage="Back to Play" />
    </Link>
  );
}

// The tutorial's end leads back to the picker (113): Grow · Play, where the card opens it. No cluster rode along into
// the tutorial, so there is no game to go on to.
// Neutral by rule: a pending, failed or closed state is a plain card with words, never a red or accent border.
function NoticeBox({ notice, actions }: { notice: Notice; actions: ReactNode }) {
  const intl = useIntl();
  return (
    <div role="status" className="mt-6 space-y-3 bg-page box-flat p-4">
      <p className="text-smycles-body text-ink">{noticeText(intl, notice.code)}</p>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
  );
}

function endMessage(game: GameSummary): ReactNode {
  switch (game.status) {
    case "open":
      return <FormattedMessage id="grow.roleplay.end.evaluating" defaultMessage="Evaluation is running …" />;
    case "abandoned":
      return <FormattedMessage id="grow.roleplay.end.abandoned" defaultMessage="This game was replaced by a restart." />;
    case "expired":
      return (
        <FormattedMessage
          id="grow.roleplay.expired"
          defaultMessage="This game has expired because it was not played for too long."
        />
      );
    default:
      return <FormattedMessage id="grow.roleplay.closed" defaultMessage="This game is closed." />;
  }
}

// What the story says it is doing, in a neutral line under the text (§4.3, §4.6).
function storyHint(state: RoleplayGameState): ReactNode {
  if (state.activity === "checking") {
    return <FormattedMessage id="grow.roleplay.story.checking" defaultMessage="Checking where the story stands …" />;
  }
  if ((state.activity === "turn" || state.activity === "narration") && state.progress === "updating_bars") {
    return <FormattedMessage id="grow.roleplay.story.updatingBars" defaultMessage="Updating your progress …" />;
  }
  const owed = state.activity === "idle" && state.notice === null && state.game?.narrationOwed != null;
  // The first opening, before any turn, begins the story rather than going on with it: nothing came before it that
  // could go on, a restarted run included.
  if (state.game?.turn === 0 && (state.activity === "narration" || owed)) {
    return <FormattedMessage id="grow.roleplay.story.begins" defaultMessage="The story begins …" />;
  }
  if (state.activity === "turn" || state.activity === "narration" || owed) {
    return <FormattedMessage id="grow.roleplay.story.goesOn" defaultMessage="The story continues …" />;
  }
  return null;
}

function RoleplayGame({ gameId }: { gameId: string }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const client = useApolloClient();
  const me = useMe();
  const location = useLocation();
  const [session] = useState(() => createRoleplaySession(gameId));
  const state = useStore(session.store);
  const [confirming, setConfirming] = useState<"restart" | null>(null);
  // A run the player restarted begins with the game: they have seen the tour (spark#1075).
  const [guideStep, setGuideStep] = useState<GuideStep>(() => (tourSeen(location.state) ? "done" : "welcome"));
  // What the field held before the tour's first pick, while the tour holds one (spark#1236): nothing picked in the tour
  // may reach the game, so the tour's end, its skip and the way back from its move step put the field back to it.
  const beforeTourPick = useRef<Draft | null>(null);

  const storyRef = useRef<HTMLElement>(null);
  const goalsRef = useRef<HTMLDivElement>(null);
  const turnRef = useRef<HTMLDivElement>(null);
  // The field's band, which sticks to the bottom of the visible area once the guide is over (22).
  const bandRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const inventoryRef = useRef<HTMLDivElement>(null);
  const castRef = useRef<HTMLDivElement>(null);
  // The card of the step on screen; the guide shows one at a time.
  const cardRef = useRef<HTMLDivElement>(null);
  const { game, view, activity, notice } = state;
  const language = game?.language ?? "de";
  const items = useMemo(() => storyItems(state.log, language), [state.log, language]);
  const idle = activity === "idle";
  // The guide shows only in a tutorial run that is open, whose story goes on and whose first turn is still to come.
  // Nothing of it is stored: a reload before the first turn starts it again. A scored game never shows it.
  const firstSteps =
    state.loaded && view !== null && game?.status === "open" && !game.storyEnded && game.turn === 0;
  const step: GuideStep = firstSteps ? guideStep : "done";
  const guiding = step !== "done";
  // Whether the guide has come as far as `at`: its element shows from that step on, sharp above the tint.
  const reached = (at: GuideStep) => GUIDE_STEPS.indexOf(step) >= GUIDE_STEPS.indexOf(at);
  const marked = (at: GuideStep) => (step === at ? "grow-guide-focus" : "grow-guide-seen");

  // Leaving the page warns only while the field holds text that was not sent, a filled template included: every sent
  // move is stored already, so nothing else is lost (spark#1075). The question offers stay and leave, never a send, and
  // the page's own moves to another game id (a restart, a new game) are no leaving. Through the tour the field is
  // switched off, so all it can hold then is the tour's try, which is no move and goes with the tour (spark#1236).
  usePendingScope("roleplay-move", {
    dirty: !guiding && state.draft.text.trim() !== "",
    leaveOnly: true,
    save: noop,
    discard: () => session.store.getState().setDraftText(""),
  });

  // When the tutorial's goals change during play, mostly with a new scene (69), the page says so (spark#1075): a "new"
  // on the goals, a line by the input until the next turn is sent, and a short pulse; a change of states alone only
  // pulses (goalsChange). It compares with the goals it showed before, so loading or reloading a scene shows nothing,
  // and the guide never does.
  const goalsKey =
    view?.situation && view.situation.goals.length > 0 ? goalsShown(view.situation.goals) : null;
  const [lastGoals, setLastGoals] = useState<string | null>(null);
  const [goalsChanged, setGoalsChanged] = useState(false);
  // The goals that appeared within the scene with the last change (goalsChange); [] when a new scene brought them all.
  const [goalsAdded, setGoalsAdded] = useState<string[]>([]);
  // Counts up while the goals box pulses, so a second pulse starts over; 0 once it is done.
  const [goalsPulse, setGoalsPulse] = useState(0);
  const goalsBoxRef = useRef<HTMLDivElement>(null);
  if (goalsKey !== null && goalsKey !== lastGoals) {
    setLastGoals(goalsKey);
    if (lastGoals !== null && step === "done") {
      const news = goalsChange(lastGoals, goalsKey);
      // A change of states alone pulses the box; the line and the "new" marks stay as they were until the next turn.
      if (news.kind !== "states") {
        setGoalsChanged(true);
        setGoalsAdded(news.kind === "added" ? news.ids : []);
      }
      setGoalsPulse((count) => count + 1);
    }
  }

  // An item the player gets during play shows twice: its words are marked in the narration that
  // brought it, and the inventory pulses like the goals, marks it "new" and is named in a line by the input, all until
  // the next turn is sent. What counts as got is the store's (arrivals): what a run brought once the story had begun,
  // so loading or reloading the page marks nothing. The guide's inventory is an example and never marked.
  const arrivals = step === "done" ? state.arrivals : NO_ARRIVALS;
  const [arrivalsSeen, setArrivalsSeen] = useState(0);
  // `run` counts up with every pulse, so a second one starts over; `on` while it pulses.
  const [inventoryPulse, setInventoryPulse] = useState({ run: 0, on: false });
  const inventoryBoxRef = useRef<HTMLDivElement>(null);
  if (arrivals.length !== arrivalsSeen) {
    setArrivalsSeen(arrivals.length);
    if (arrivals.length > arrivalsSeen) setInventoryPulse((pulse) => ({ run: pulse.run + 1, on: true }));
  }

  // Someone who comes into the story during play shows the same way (82): their name, or else the word their
  // description names them by, marked in the narration that brought them, the characters box pulsing with a "new" on
  // them, and a line by the input, all until the next turn is sent. What counts as new is the store's (newcomers): a
  // character a run brought once the story had begun. The guide's characters are an example and never marked.
  const newcomers = step === "done" ? state.newcomers : NO_NEWCOMERS;
  const [newcomersSeen, setNewcomersSeen] = useState(0);
  const [castPulse, setCastPulse] = useState({ run: 0, on: false });
  const castBoxRef = useRef<HTMLDivElement>(null);
  if (newcomers.length !== newcomersSeen) {
    setNewcomersSeen(newcomers.length);
    if (newcomers.length > newcomersSeen) setCastPulse((pulse) => ({ run: pulse.run + 1, on: true }));
  }

  const [handOver, setHandOver] = useState(false);
  // Progress, inventory and characters fold when a game begins and open one by one, the progress like the other two, so
  // the story has the room at the start. The page holds it, not each box, so a box that pulses for something new and is
  // drawn anew stays as the player left it. The guide's examples open by themselves.
  const [opened, setOpened] = useState({ progress: false, inventory: false, cast: false });
  const toggleBox = (box: keyof typeof opened) => () => setOpened((shown) => ({ ...shown, [box]: !shown[box] }));

  useEffect(() => {
    session.start();
    return () => session.stop();
  }, [session]);

  // Another tab may have played meanwhile (§4.5).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void session.refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [session]);

  // The tour's try leaves the game nothing (spark#1236): the pick goes, and so do the words its follow-ups inserted,
  // since the field stays switched off through the tour and they are all it can hold then. The game's own draft from
  // before the first pick is what stays. The open panel of options closes as the field is switched on (TurnInput).
  const discardTourPick = () => {
    const before = beforeTourPick.current;
    beforeTourPick.current = null;
    if (!before) return;
    const store = session.store.getState();
    store.setDraftText(before.text);
    if (before.picked) store.pickFollowUp();
    else store.dropPick();
  };
  // However the guide ends, the try goes with it: by "Let's go" and the skip (startGame) at once, and here when the
  // run leaves its first steps on its own, such as when another tab's turn arrives or the game closes meanwhile.
  useEffect(() => {
    if (!guiding) discardTourPick();
  });

  // The tint belongs to the guide and goes with it: after its last step, when the tour is skipped, when the run ends
  // and when the page leaves.
  useEffect(() => {
    if (!guiding) return;
    document.body.classList.add(GUIDE_SPOTLIGHT);
    return () => document.body.classList.remove(GUIDE_SPOTLIGHT);
  }, [guiding]);

  // Each step brings the element it explains into view; its card follows right below it. Where the two do not fit into
  // the visible area together, the card's end comes to the visible end instead, the element's lower part above it: its
  // way on holds the focus, and a keyboard player would otherwise press Enter, or Space to scroll on, on a button they
  // cannot see. That is where the card would come to if it followed the element by the least scroll, decided here in
  // one scroll, since a second would cancel the first. The card brings itself into view too whenever its way on takes
  // the focus, as the situation's does once the opening is told; on a new step this runs after the card's own, so the
  // step decides.
  useEffect(() => {
    const targets: Partial<Record<GuideStep, HTMLElement | null>> = {
      situation: storyRef.current,
      goals: goalsRef.current,
      turn: turnRef.current,
      progress: progressRef.current,
      inventory: inventoryRef.current,
      cast: castRef.current,
    };
    const target = targets[step];
    if (!target) return;
    const card = cardRef.current;
    if (card && card.getBoundingClientRect().bottom - target.getBoundingClientRect().top > visibleHeight(target)) {
      card.scrollIntoView?.({ block: "end", behavior: "smooth" });
    } else {
      target.scrollIntoView?.({ block: "start", behavior: "smooth" });
    }
  }, [step]);

  useEffect(() => {
    if (goalsPulse === 0) return;
    const timer = window.setTimeout(() => setGoalsPulse(0), PULSE_MS);
    return () => window.clearTimeout(timer);
  }, [goalsPulse]);

  useEffect(() => {
    if (!inventoryPulse.on) return;
    const timer = window.setTimeout(() => setInventoryPulse((pulse) => ({ ...pulse, on: false })), PULSE_MS);
    return () => window.clearTimeout(timer);
  }, [inventoryPulse]);

  useEffect(() => {
    if (!castPulse.on) return;
    const timer = window.setTimeout(() => setCastPulse((pulse) => ({ ...pulse, on: false })), PULSE_MS);
    return () => window.clearTimeout(timer);
  }, [castPulse]);

  // "Let's go" puts the focus in the field once the render that switches it on is done (a switched off field takes no
  // focus), and the hand over's outline comes off once it has faded, or with the page.
  useEffect(() => {
    if (!handOver) return;
    turnRef.current?.querySelector("textarea")?.focus();
    const timer = window.setTimeout(() => setHandOver(false), prefersReducedMotion() ? HANDOVER_STILL_MS : HANDOVER_MS);
    return () => window.clearTimeout(timer);
  }, [handOver]);

  // Whether the player follows the story while it streams: a player who scrolled up on purpose is not pulled back
  // down. They do until they scroll up while the story box's end stands below the visible
  // area, so a scroll toward the end never stops it, the page's own glide included. At the end they follow again, and
  // so they do once they send a move or try a stalled narration again. The scrolls are those of the area the page
  // scrolls in, AppShell's <main>, or the window where the page stands in none, read once the story box shows. Safari
  // moves that area a frame before it reports the scroll (measured in WebKit), so a piece streamed in meanwhile looks
  // at the scroll too: a scroll up not reported yet stops the follow there rather than being undone by it.
  const follow = useRef<Follow>({ on: true, seen: 0, glideTo: null });
  const storyShown = state.loaded && game !== null && view !== null;
  useEffect(() => {
    const story = storyRef.current;
    if (!story) return;
    const area: EventTarget = story.closest("main") ?? window;
    follow.current.seen = scrolledDown(story);
    const onScroll = () => lookAtTheScroll(story, follow.current);
    area.addEventListener("scroll", onScroll, { passive: true });
    return () => area.removeEventListener("scroll", onScroll);
  }, [storyShown]);

  // Bring the story box's end into view when a run starts and with each part it begins to tell, though not under the
  // welcome, which is read at the top. There the field pinned to the bottom (22) stands in its own place, so what the
  // story shows at that moment stands right above it, never behind it. In between, a piece streamed into the part being
  // told only makes that part longer (narrate). Where such a piece leaves the end below the visible area, the end
  // follows it too, at once rather than in a glide, so a player who was at the end stays there while it streams: a
  // glide for each piece lags behind the text, and a jump is no animation, so reduced motion needs nothing more. All of
  // this only while the player follows; a step of the guide moves the page anyway, so on one the end comes into view as
  // before and the player follows from there, even where the story grows on while the glide is still on its way. A
  // notice comes into view the same way once the guide is over, wherever the player is: it stands right above the
  // pinned field, which would cover it otherwise, and a stalled narration's "Try again" is the only way on.
  const lastPartLength = state.live.at(-1)?.text.length ?? 0;
  // What the story box showed when this last ran: how many parts it was telling, how long the last one was, and the
  // guide's step. That tells a piece streamed into the same part apart from a run's start, a new part and a step.
  const shown = useRef({ parts: 0, length: 0, step });
  useEffect(() => {
    const before = shown.current;
    shown.current = { parts: state.live.length, length: lastPartLength, step };
    const story = storyRef.current;
    if (step === "welcome" || !story) return;
    if (notice !== null && !guiding) {
      story.scrollIntoView?.({ block: "end", behavior: "smooth" });
      return;
    }
    if (activity !== "turn" && activity !== "narration") return;
    lookAtTheScroll(story, follow.current);
    const stepped = step !== before.step;
    if (!stepped && state.live.length === before.parts && lastPartLength > before.length) {
      // Where the jump lands is not taken as seen: while a glide is still on its way, Safari puts the glide's own place
      // back after it (measured in WebKit), and that would read as a scroll up.
      if (follow.current.on && belowTheEnd(story) > AT_END_PX) story.scrollIntoView?.({ block: "end", behavior: "auto" });
    } else if (follow.current.on || stepped) {
      // From below the story box's end this glide goes up: `glideTo` is where it comes to rest (Follow).
      const below = belowTheEnd(story);
      follow.current.on = true;
      follow.current.glideTo = below < -AT_END_PX ? Math.max(0, scrolledDown(story) + below) : null;
      story.scrollIntoView?.({ block: "end", behavior: "smooth" });
    }
  }, [activity, state.live.length, lastPartLength, step, notice, guiding]);

  // The player's own move, or "Try again", brings the end back (see `follow`), from wherever they have scrolled to.
  const followAgain = () => {
    follow.current.on = true;
    if (storyRef.current) follow.current.seen = scrolledDown(storyRef.current);
  };

  const openGame = (id: string | null) => {
    if (id) navigate(gameHref(id), { replace: true });
  };
  const continueTo = async () => openGame(await session.continueTo(client));

  const title = <FormattedMessage id="grow.roleplay.title" defaultMessage="Roleplay" />;

  // What a notice offers. `backToPlay` adds the way out where the page has nothing else to show.
  const noticeActions = (current: Notice, backToPlay: boolean) => (
    <>
      {current.retry ? (
        <Button
          variant="secondary"
          size="compact"
          disabled={!idle}
          onClick={() => {
            followAgain();
            void session.retry();
          }}
        >
          <FormattedMessage id="common.tryAgain" defaultMessage="Try again" />
        </Button>
      ) : null}
      {current.code === "updateNeeded" ? (
        <Button variant="secondary" size="compact" onClick={() => window.location.reload()}>
          <FormattedMessage id="grow.roleplay.reload" defaultMessage="Reload" />
        </Button>
      ) : null}
      {current.code === "expired" ? (
        <Button size="compact" disabled={!idle} onClick={() => void continueTo()}>
          <FormattedMessage id="grow.roleplay.newGame" defaultMessage="Start a new game" />
        </Button>
      ) : null}
      {backToPlay || current.code === "notFound" ? (
        <BackToPlay />
      ) : null}
    </>
  );

  if (!state.loaded || !game || !view) {
    return (
      <Frame title={title}>
        {notice ? (
          <NoticeBox notice={notice} actions={noticeActions(notice, true)} />
        ) : (
          <p className="mt-8 text-smycles-body text-ink-muted" aria-busy="true">
            <FormattedMessage id="common.loading" defaultMessage="Loading…" />
          </p>
        )}
      </Frame>
    );
  }

  const situation = view.situation;
  const showInput = inputOpen(state);
  // The end shows once the closing is told and the run that told it has settled, never beside a streaming story.
  const settledRun = activity === "idle" || activity === "busy";
  const ended = settledRun && game.narrationOwed === null && (game.status !== "open" || game.storyEnded);
  // The story has ended and Genesis has not finished the run yet: its evaluation is running.
  const evaluating = game.status === "open" && game.storyEnded;
  // Only an open or a finished game can be restarted; the session refuses any other without asking Genesis.
  const restartable = game.status === "open" || game.status === "finished";
  const clock = storyClock(view.dateTime);
  // The situation step leads on only once the opening is told; until then the story's own hint or notice speaks.
  const openingTold = idle && notice === null && game.narrationOwed === null && situation !== null;
  // While the opening is still being told the situation step says so, so its switched off button does not look like a
  // fault („Deine Situation wird gerade erzählt …“). A notice, such as a
  // narration that failed, speaks for itself instead.
  const openingTelling = notice === null && (activity === "narration" || game.narrationOwed !== null);
  // Until the opening is told the scene is unknown; a tutorial's scene has its courses of action (4; its clickable
  // options since spark#1075) and shows its goals (69), so the guide expects both meanwhile.
  const optionCount = situation?.options.length ?? 0;
  const hasCourses = situation === null || situation.poles.length > 0 || optionCount > 0;
  const hasGoals = situation === null || situation.goals.length > 0;
  const poleCount = situation?.poles.length ?? 0;
  const firstName = me.firstName.trim();
  // From the move step on the field shows, switched off until the guide is over, so no turn skips
  // the steps still to come. "Let's go" hands over to that field, and so does „Tour überspringen“, which lands exactly
  // like it (spark#1075); a run opened past its first turn, a restarted run and a scored game never mark it.
  const startGame = () => {
    discardTourPick();
    setGuideStep("done");
    setHandOver(true);
  };
  const goalsNews = goalsChanged && !guiding && situation !== null && situation.goals.length > 0;
  // A goal that appeared within the scene is named in the line by the input and marked "new" in the list.
  const addedNames = goalsNews
    ? goalRows(situation.goals)
        .filter(([id]) => goalsAdded.includes(id))
        .map(([, de, en]) => (language === "de" ? de : en))
    : [];
  // In the tutorial the courses of action show only while the guide explains them; once it hands over, the field stands
  // alone, because each situation's courses would have to be curated by hand first, and uncurated ones irritate the
  // player. A scored game keeps them.
  // That holds for the poles only: a scene's clickable courses of action (spark#1075) show in the tutorial and in a game
  // alike. In the guide's move step the player tries them as in the game: a follow-up clicked there puts its template
  // into the field, which stays switched off with "Send" until the guide is over. It is only a try: however the guide
  // ends, and when the player goes back from the move step, the pick and its words go again, so the first move needs
  // its own click (spark#1236, discardTourPick). In every other step they are off.
  const coursesShown = !guiding ? [] : (situation?.poles ?? []);
  // The line by the input leads to the goals, which sit below the story on a narrow screen, and pulses them again.
  // "nearest" leaves the story where it is when the goals are already in view, as in the sticky column.
  const showGoals = () => {
    goalsBoxRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    setGoalsPulse((count) => count + 1);
  };
  // The items got since the last turn that the player still holds, by id, and the words to mark per run.
  const held = new Map(heldItems(view.inventory).map((item) => [item.id, item]));
  const freshIds = new Set(arrivals.filter((arrival) => held.has(arrival.id)).map((arrival) => arrival.id));
  const freshNames = [...new Set([...freshIds].map((id) => held.get(id)!.name[language]))];
  const marks = new Map<string, StoryMark[]>();
  // A name marks in the forms a narration bends it into, a newcomer's head noun only as a whole word (markedParts).
  const mark = (runId: string, next: StoryMark) => marks.set(runId, [...(marks.get(runId) ?? []), next]);
  for (const arrival of arrivals) mark(arrival.runId, { word: arrival.name[language], whole: false });
  const itemsNews = freshNames.length > 0;
  // As the goals' line: the inventory sits below the story on a narrow screen.
  const showInventory = () => {
    setOpened((shown) => ({ ...shown, inventory: true }));
    inventoryBoxRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    setInventoryPulse((pulse) => ({ run: pulse.run + 1, on: true }));
  };
  // The characters new since the last turn, by id, named as the characters box names them now, and the word each one's
  // run told them by.
  const listed = new Map(view.cast.map((character) => [character.id, character]));
  const newIds = new Set(newcomers.filter((newcomer) => listed.has(newcomer.id)).map((newcomer) => newcomer.id));
  const newNames = [...new Set([...newIds].map((id) => characterName(listed.get(id)!, language)))];
  for (const newcomer of newcomers) mark(newcomer.runId, { word: newcomer.word[language], whole: newcomer.whole });
  const castNews = newNames.length > 0;
  // As the inventory's line: the characters sit below the story on a narrow screen.
  const showCast = () => {
    setOpened((shown) => ({ ...shown, cast: true }));
    castBoxRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    setCastPulse((pulse) => ({ run: pulse.run + 1, on: true }));
  };
  // The field keeps the focus however the move goes out: a click on Send moves it to the button, which then waits
  // switched off while the story runs, so the player would have to click back into the field to write on. Found as
  // "Let's go" finds it, and with no scroll, since the field stands in view where it is.
  const sendTurn = () => {
    setGoalsChanged(false);
    followAgain();
    turnRef.current?.querySelector("textarea")?.focus({ preventScroll: true });
    void session.send(client);
  };
  // „Tour überspringen“ ends the tour alone and asks nothing; the run goes on (spark#1075).
  const skipButton = (
    <Button variant="secondary" size="compact" onClick={startGame}>
      <FormattedMessage id="grow.roleplay.tour.skip" defaultMessage="Skip the tour" />
    </Button>
  );
  // The plane game's shown name (spark#1075).
  const subtitle = <FormattedMessage id="grow.roleplay.planeGame.title" defaultMessage="The Crying Baby" />;

  // A control above the pinned field that takes the keyboard focus while the field covers it, a notice's "Try again" or
  // a line by the input, comes out from behind it (WCAG 2.4.11), even after the player has scrolled: the story box's end
  // into view sets the field in its own place, below the control. The browser does not scroll for it, since a control
  // behind the field is inside the visible area. In the guide the field stands in the story's flow and covers nothing.
  const uncover = (event: FocusEvent<HTMLElement>) => {
    const band = bandRef.current;
    if (!band || band.contains(event.target)) return;
    if (event.target.getBoundingClientRect().bottom > band.getBoundingClientRect().top) {
      storyRef.current?.scrollIntoView?.({ block: "end" });
    }
  };

  // The story keeps its element from the guide into the game (`key`), so the field the player may already have
  // typed in during the guide stays where it is, focus and all. Like Save Studio's cards it ends with its content
  // rather than stretching to the side column's height, and it shares the side boxes' padding.
  const storySection = (
    <section
      key="story"
      ref={storyRef}
      onFocus={uncover}
      aria-label={intl.formatMessage({ id: "grow.roleplay.story.label", defaultMessage: "Story" })}
      className={`min-w-0 self-start bg-surface box p-5${
        guiding && reached("situation") ? ` ${marked("situation")}` : ""
      }`}
    >
      {clock ? (
        <div className="mb-4 flex justify-end">
          <time
            dateTime={view.dateTime}
            title={intl.formatMessage({ id: "grow.roleplay.story.clock", defaultMessage: "Time in the story" })}
            className="smycles-field-meta tabular-nums text-ink-muted"
          >
            {intl.formatDate(clock, {
              weekday: "short",
              day: "numeric",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              timeZone: "UTC",
            })}
          </time>
        </div>
      ) : null}
      <StoryFeed
        items={items}
        live={state.live}
        pending={state.pending}
        unsent={state.unsent}
        truncated={state.truncated}
        hint={storyHint(state)}
        marks={marks}
        liveMarks={state.runId ? marks.get(state.runId) : undefined}
      />
      {notice ? <NoticeBox notice={notice} actions={noticeActions(notice, false)} /> : null}
      {/* Always there, so a screen reader hears a line when it comes. */}
      <div aria-live="polite" className={goalsNews || itemsNews || castNews ? "mt-6 flex flex-col items-start gap-1" : undefined}>
        {goalsNews ? (
          <button
            type="button"
            onClick={showGoals}
            className="text-left smycles-field-meta text-ink underline underline-offset-2 hover:text-ink focus-visible:outline focus-visible:outline-1 focus-visible:outline-ink-accent-ui"
          >
            {addedNames.length > 0 ? (
              <FormattedMessage
                id="grow.roleplay.goals.added"
                defaultMessage="{count, plural, one {New goal: {goals}} other {New goals: {goals}}}"
                values={{ count: addedNames.length, goals: intl.formatList(addedNames, { type: "conjunction" }) }}
              />
            ) : (
              <FormattedMessage id="grow.roleplay.goals.changed" defaultMessage="Your goals have changed." />
            )}
          </button>
        ) : null}
        {itemsNews ? (
          <button
            type="button"
            onClick={showInventory}
            className="text-left smycles-field-meta text-ink underline underline-offset-2 hover:text-ink focus-visible:outline focus-visible:outline-1 focus-visible:outline-ink-accent-ui"
          >
            <FormattedMessage
              id="grow.roleplay.inventory.changed"
              defaultMessage="New in your inventory: {items}"
              values={{ items: intl.formatList(freshNames, { type: "conjunction" }) }}
            />
          </button>
        ) : null}
        {castNews ? (
          <button
            type="button"
            onClick={showCast}
            className="text-left smycles-field-meta text-ink underline underline-offset-2 hover:text-ink focus-visible:outline focus-visible:outline-1 focus-visible:outline-ink-accent-ui"
          >
            <FormattedMessage
              id="grow.roleplay.cast.changed"
              defaultMessage="New among the characters: {names}"
              values={{ names: intl.formatList(newNames, { type: "conjunction" }) }}
            />
          </button>
        ) : null}
      </div>
      {/* In the guide's move step this wrapper is the marked element. In the game it gives up its own box (`contents`),
          so the field's band can stick to the bottom of the visible area all along the story box (22). */}
      {reached("turn") && showInput && situation ? (
        <div ref={turnRef} className={guiding ? (step === "turn" ? "grow-guide-focus" : undefined) : "contents"}>
          <TurnInput
            poles={coursesShown}
            options={situation.options}
            scene={game.scene}
            needsPick={!guiding && pickMissing(state)}
            language={language}
            text={state.draft.text}
            maxLength={state.limits.maxTextLength}
            canSend={!guiding && canSend(state)}
            locked={guiding}
            optionsLocked={guiding && step !== "turn"}
            pinned={!guiding}
            bandRef={bandRef}
            highlight={handOver}
            onText={(text) => state.setDraftText(text)}
            onSend={sendTurn}
            onPicked={() => {
              if (guiding && beforeTourPick.current === null) beforeTourPick.current = session.store.getState().draft;
              state.pickFollowUp();
            }}
            onUnpicked={() => state.dropPick()}
          />
        </div>
      ) : null}
      {ended ? (
        <div className="mt-6 space-y-3 bg-page box-flat p-4">
          {/* A finished game has no line of its own: the summary below says how it ended („spark#1075, Entscheidung 127.63“). */}
          {game.status === "finished" ? null : <p className="text-smycles-body text-ink">{endMessage(game)}</p>}
          {game.status === "finished" && game.summary ? (
            <GameEndSummary summary={game.summary} gameId={game.id} />
          ) : null}
          <div className="flex flex-wrap gap-2">
            {/* No restart while the evaluation runs (an ended story Genesis has not finished yet): the run is complete and
                its result on its way, so the card offers the restart again once the evaluation is done (spark#1236). */}
            {game.status === "finished" ? (
              <Button variant="secondary" size="compact" disabled={!idle} onClick={() => setConfirming("restart")}>
                <FormattedMessage id="grow.roleplay.restart.button" defaultMessage="Restart" />
              </Button>
            ) : null}
            {game.status === "expired" ? (
              <Button size="compact" disabled={!idle} onClick={() => void continueTo()}>
                <FormattedMessage id="grow.roleplay.newGame" defaultMessage="Start a new game" />
              </Button>
            ) : null}
            <BackToPlay />
          </div>
        </div>
      ) : null}
    </section>
  );

  const exampleLabel = <FormattedMessage id="grow.roleplay.intro.exampleLabel" defaultMessage="Example" />;
  // The game's progress, or in the guide its example (`example`), marked as one where the game says "updating". It folds
  // like the inventory (see `opened`); the offer to evaluate stands below it, so a folded box never hides it.
  const progressBox = (example = false) => (
    <section>
      <Disclosure
        title={<FormattedMessage id="grow.roleplay.bars.title" defaultMessage="Progress" />}
        headerAction={
          example ? (
            <span className="smycles-field-meta text-ink-muted">{exampleLabel}</span>
          ) : game.bars.state === "pending" ? (
            <span className="smycles-field-meta text-ink-muted">
              <FormattedMessage id="grow.roleplay.bars.pending" defaultMessage="updating" />
            </span>
          ) : undefined
        }
        defaultOpen={example}
        open={example ? undefined : opened.progress}
        onToggle={example ? undefined : toggleBox("progress")}
        className="bg-surface"
      >
        {example ? (
          <PowerBars bars={exampleBars(game.bars)} language={language} />
        ) : (
          <PowerBars bars={game.bars} language={language} />
        )}
      </Disclosure>
    </section>
  );

  return (
    <Frame subtitle={subtitle} title={title}>
      {guiding ? (
        <div className="mt-8 max-w-3xl space-y-6">
          {step === "welcome" ? (
            <GuideCard
              ref={cardRef}
              action={<FormattedMessage id="grow.roleplay.intro.toSituation" defaultMessage="On to the situation" />}
              onAction={() => setGuideStep("situation")}
              skip={skipButton}
            >
              <FormattedMessage
                id="grow.roleplay.intro.welcome"
                defaultMessage="{named, select, yes {Hey {name},} other {Hey,}} this is Grow's roleplay game. In this plane story, you decide what you do and say. You chose your Powers before it began."
                values={{ named: firstName ? "yes" : "no", name: firstName }}
              />
            </GuideCard>
          ) : null}
          {storySection}
          {step === "situation" ? (
            <GuideCard
              ref={cardRef}
              pointsUp
              action={
                hasCourses ? (
                  <FormattedMessage id="grow.roleplay.intro.toSuggestions" defaultMessage="On to the courses of action" />
                ) : (
                  <FormattedMessage id="grow.roleplay.intro.toTurn" defaultMessage="On to your move" />
                )
              }
              actionDisabled={!openingTold}
              onAction={() => setGuideStep("turn")}
              onBack={() => setGuideStep("welcome")}
              skip={skipButton}
            >
              <FormattedMessage
                id="grow.roleplay.intro.situation"
                defaultMessage="This is your situation. Take your time to read where you are and who is around you."
              />
              {openingTelling ? (
                <span className="mt-2 block smycles-field-meta text-ink-muted">
                  <FormattedMessage id="grow.roleplay.intro.situationTelling" defaultMessage="Your situation is being told …" />
                </span>
              ) : null}
            </GuideCard>
          ) : null}
          {step === "turn" ? (
            <GuideCard
              ref={cardRef}
              pointsUp
              action={
                hasGoals ? (
                  <FormattedMessage id="grow.roleplay.intro.toGoals" defaultMessage="On to the goals" />
                ) : (
                  <FormattedMessage id="grow.roleplay.intro.toProgress" defaultMessage="On to your progress" />
                )
              }
              onAction={() => setGuideStep(hasGoals ? "goals" : "progress")}
              onBack={() => {
                discardTourPick();
                setGuideStep("situation");
              }}
              skip={skipButton}
            >
              {optionCount > 0 ? (
                <FormattedMessage
                  id="grow.roleplay.intro.turnOptions"
                  defaultMessage={'Before every decision you see courses of action. Pick one, then how you carry it out; that puts the start of your move in the field. "Back" by the courses of action takes your choice back. In the field you describe how and why you do it and how you feel about it.'}
                />
              ) : poleCount > 0 ? (
                <FormattedMessage
                  id="grow.roleplay.intro.turn"
                  defaultMessage="Before every decision you see {count, plural, one {a course of action} =2 {two courses of action} other {# courses of action}} for where the story can go. {count, plural, one {It is only a suggestion} other {They are only suggestions}} for orientation: you are not bound to {count, plural, one {it} other {them}} and can do whatever you like. Describe in the field how you feel in the situation, what you think and what you do, and why in each case."
                  values={{ count: poleCount }}
                />
              ) : (
                <FormattedMessage
                  id="grow.roleplay.intro.turnNoPoles"
                  defaultMessage="Before every decision you describe in the field how you feel in the situation, what you think and what you do, and why in each case."
                />
              )}{" "}
              <FormattedMessage
                id="grow.roleplay.intro.turnKeys"
                defaultMessage={'You send your move with "Send".'}
              />
              {optionCount > 0 ? (
                <>
                  {" "}
                  <FormattedMessage
                    id="grow.roleplay.intro.turnTry"
                    defaultMessage="Try it: open a course of action and choose how you carry it out."
                  />
                </>
              ) : null}
            </GuideCard>
          ) : null}
          {reached("goals") && situation && hasGoals ? (
            <div ref={goalsRef} className={marked("goals")}>
              <GoalList goals={situation.goals} language={language} />
            </div>
          ) : null}
          {step === "goals" ? (
            <GuideCard
              ref={cardRef}
              pointsUp
              action={<FormattedMessage id="grow.roleplay.intro.toProgress" defaultMessage="On to your progress" />}
              onAction={() => setGuideStep("progress")}
              onBack={() => setGuideStep("turn")}
              skip={skipButton}
            >
              <FormattedMessage
                id="grow.roleplay.intro.goals"
                defaultMessage="At the top are your two main goals for the whole flight. Reach one of them and the game is over. The smaller goals beneath them help you find your way. Depending on what you do and say, goals appear, are reached or drop away."
              />
            </GuideCard>
          ) : null}
          {reached("progress") ? (
            <div ref={progressRef} className={marked("progress")}>
              {progressBox(true)}
            </div>
          ) : null}
          {step === "progress" ? (
            <GuideCard
              ref={cardRef}
              pointsUp
              action={<FormattedMessage id="grow.roleplay.intro.toInventory" defaultMessage="On to the inventory" />}
              onAction={() => setGuideStep("inventory")}
              onBack={() => setGuideStep(hasGoals ? "goals" : "turn")}
              skip={skipButton}
            >
              <FormattedMessage
                id="grow.roleplay.intro.progress"
                defaultMessage="Our AI assesses your answers. For each chosen Power, the bars show how many questions your answers have covered so far. Powers with enough clues receive a value in your Smart Analysis after the story."
              />
            </GuideCard>
          ) : null}
          {reached("inventory") ? (
            <div ref={inventoryRef} className={marked("inventory")}>
              <InventoryPanel inventory={exampleInventory(intl)} language={language} note={exampleLabel} />
            </div>
          ) : null}
          {step === "inventory" ? (
            <GuideCard
              ref={cardRef}
              pointsUp
              action={<FormattedMessage id="grow.roleplay.intro.toCast" defaultMessage="On to the characters" />}
              onAction={() => setGuideStep("cast")}
              onBack={() => setGuideStep("progress")}
              skip={skipButton}
            >
              {/* A lead, then the states and the ways to get an item as two lists (spark#1236), with the list style of
                  the app's other guide texts (the repo's list-disc and marker pattern). */}
              <p>
                <FormattedMessage
                  id="grow.roleplay.intro.inventory.lead"
                  defaultMessage="This is what your inventory could look like. Everything you carry is listed here. Depending on its kind, an item has a state:"
                />
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 marker:text-ink-muted">
                <li>
                  <FormattedMessage id="grow.roleplay.intro.inventory.state1" defaultMessage="Food is fresh, questionable or spoiled." />
                </li>
                <li>
                  <FormattedMessage
                    id="grow.roleplay.intro.inventory.state2"
                    defaultMessage="A device has a battery that you can charge wherever there is power."
                  />
                </li>
                <li>
                  <FormattedMessage id="grow.roleplay.intro.inventory.state3" defaultMessage="Some things you can only use a few more times." />
                </li>
                <li>
                  <FormattedMessage
                    id="grow.roleplay.intro.inventory.state4"
                    defaultMessage="Other things wear out with use and over time, from good through worn to broken. Once they are broken, you can no longer use them."
                  />
                </li>
              </ul>
              <p className="mt-3">
                <FormattedMessage id="grow.roleplay.intro.inventory.waysLead" defaultMessage="You get items in four ways:" />
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 marker:text-ink-muted">
                <li>
                  <FormattedMessage id="grow.roleplay.intro.inventory.way1" defaultMessage="Some you have from the start." />
                </li>
                <li>
                  <FormattedMessage id="grow.roleplay.intro.inventory.way2" defaultMessage="A character gives you others." />
                </li>
                <li>
                  <FormattedMessage id="grow.roleplay.intro.inventory.way3" defaultMessage="Some you take yourself." />
                </li>
                <li>
                  <FormattedMessage
                    id="grow.roleplay.intro.inventory.way4"
                    defaultMessage="You can also take something away from a character. That rarely goes without consequences, though."
                  />
                </li>
              </ul>
              {view.inventory.length === 0 ? (
                <p className="mt-3">
                  <FormattedMessage
                    id="grow.roleplay.intro.inventoryEmpty"
                    defaultMessage="Your own inventory is still empty, but that can change as the story goes on."
                  />
                </p>
              ) : null}
            </GuideCard>
          ) : null}
          {step === "cast" ? (
            <>
              <div ref={castRef} className="grow-guide-focus">
                <CastPanel cast={exampleCast(intl)} language={language} note={exampleLabel} />
              </div>
              <GuideCard
                ref={cardRef}
                pointsUp
                action={<FormattedMessage id="grow.roleplay.intro.start" defaultMessage="Let's go" />}
                onAction={startGame}
                onBack={() => setGuideStep("inventory")}
                skip={skipButton}
              >
                <FormattedMessage
                  id="grow.roleplay.intro.cast"
                  defaultMessage="Everyone you meet or hear of is listed here. At first you know little about them. By talking to them and watching them, you learn their name, their mood, their relationship to you and what they carry. How someone seems is not necessarily how they feel."
                />
                {view.cast.length === 0 ? (
                  <>
                    {" "}
                    <FormattedMessage
                      id="grow.roleplay.intro.castEmpty"
                      defaultMessage="You have not met anyone yet, but that will change as the story goes on."
                    />
                  </>
                ) : null}
              </GuideCard>
            </>
          ) : null}
        </div>
      ) : (
        // Two columns on a desktop screen (the persistent sidebar's `lg`), whatever the assistant panel leaves of
        // <main>: a viewport variant on purpose (@adr SPARK-0020-container-queries-as-the-responsive-primitive-inside-main@2
        // allows it where the screen class matters), so that opening the panel never folds the side column under the
        // story. Below 48rem of <main> the columns keep that width and <main> scrolls sideways (spark#1075).
        // A phone keeps one column: nothing sticks there, it would cover the story.
        <div className="mt-8 grid grid-cols-1 gap-6 lg:min-w-[48rem] lg:grid-cols-[minmax(0,1.7fr)_minmax(16rem,1fr)]">
          {storySection}
          {/* Beside the story the column stays in view while the story scrolls, the goals at its top; taller than the
              visible area, it scrolls on its own, and the goals stay at its top while it does (spark#1075). Its padding keeps
              the goals' pulse outline inside the scroll box, the negative margin keeps the column where it was; the
              goals' band reaches into that padding and sticks at its outer edge, so nothing scrolls by above or beside
              them. */}
          <aside className="min-w-0 space-y-6 lg:sticky lg:top-3 lg:-m-3 lg:max-h-[calc(100dvh-6.5rem)] lg:self-start lg:overflow-y-auto lg:overscroll-contain lg:p-3">
            {situation && situation.goals.length > 0 ? (
              <div className="bg-page lg:sticky lg:-top-3 lg:z-10 lg:-mx-3 lg:-mt-3 lg:mb-3 lg:p-3">
                <div key={`goals-${goalsPulse}`} ref={goalsBoxRef} className={goalsPulse > 0 ? "grow-goals-changed" : undefined}>
                  <GoalList goals={situation.goals} language={language} fresh={goalsNews} added={goalsNews ? goalsAdded : undefined} />
                </div>
              </div>
            ) : null}
            {progressBox()}
            {view.conditions.length > 0 ? (
              <section className="bg-surface box p-5">
                <BoxHeader title={<FormattedMessage id="grow.roleplay.conditions.title" defaultMessage="Your condition" />} />
                <ul className="space-y-1 text-smycles-body text-ink">
                  {view.conditions.map((condition, index) => (
                    <li key={index}>{conditionLabel(intl, condition)}</li>
                  ))}
                </ul>
              </section>
            ) : null}
            <div
              key={`inventory-${inventoryPulse.run}`}
              ref={inventoryBoxRef}
              className={inventoryPulse.on ? "grow-inventory-changed" : undefined}
            >
              <InventoryPanel
                inventory={view.inventory}
                language={language}
                fresh={freshIds}
                open={opened.inventory}
                onToggle={toggleBox("inventory")}
              />
            </div>
            <div key={`cast-${castPulse.run}`} ref={castBoxRef} className={castPulse.on ? "grow-cast-changed" : undefined}>
              <CastPanel cast={view.cast} language={language} fresh={newIds} open={opened.cast} onToggle={toggleBox("cast")} />
            </div>
            {game.status === "open" && !game.storyEnded ? (
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" size="compact" disabled={!idle} onClick={() => setConfirming("restart")}>
                  <FormattedMessage id="grow.roleplay.restart.button" defaultMessage="Restart" />
                </Button>
              </div>
            ) : null}
          </aside>
        </div>
      )}
      {/* A dialog opened during play whose story ends meanwhile waits for the evaluation, as the end card does: its
          confirm comes back once Genesis has finished the run (spark#1236). A game that expired or was replaced by a
          restart meanwhile cannot be restarted at all, so its confirm stays off and only Cancel leads on. */}
      <ConfirmDialog
        open={confirming === "restart"}
        title={<FormattedMessage id="grow.roleplay.restart.confirmTitle" defaultMessage="Restart this game?" />}
        confirmDisabled={evaluating || !restartable}
        message={!restartable ? (
          <FormattedMessage
            id="grow.roleplay.restart.unavailableMessage"
            defaultMessage="This game can no longer be restarted."
          />
        ) : evaluating ? (
          <FormattedMessage
            id="grow.roleplay.restart.evaluatingMessage"
            defaultMessage="Evaluation is running. You can restart once it is finished."
          />
        ) : game.status === "finished" ? (
          <FormattedMessage
            id="grow.roleplay.restart.finishedMessage"
            defaultMessage="Your completed result stays saved. You will play this story again from the beginning."
          />
        ) : (
          <FormattedMessage
            id="grow.roleplay.restart.confirmMessage"
            defaultMessage="Your progress in this game will be lost. Values from earlier games remain."
          />
        )}
        confirmLabel={<FormattedMessage id="grow.roleplay.restart.button" defaultMessage="Restart" />}
        onCancel={() => setConfirming(null)}
        onConfirm={async () => {
          setConfirming(null);
          const id = await session.restart(client);
          if (id) navigate(gameHref(id), { replace: true, state: TOUR_SEEN_STATE });
        }}
      />
    </Frame>
  );
}
